import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Save, Loader2, Upload, X, Image as ImageIcon, Eye, EyeOff,
  Calendar, HardHat, Users2, CheckSquare, Square, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("admin_token") || localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const empty = {
  client_id: "",
  title: "",
  context: "",
  location_city: "",
  location_postal_code: "",
  service_type: "",
  duration_days: 0,
  started_at: "",
  ended_at: "",
  team_member_ids: [],
  before_photos: [],
  after_photos: [],
  benefits: "",
  internal_notes: "",
  publish_consent: false,
  is_published: false,
};

export default function ChantierEditAdmin() {
  const { wid } = useParams();
  const isNew = !wid || wid === "new";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ ...empty, client_id: searchParams.get("client_id") || "" });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({ type: "particulier", first_name: "", last_name: "", email: "", phone: "", company_name: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  // Load lists
  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([
          api.get("/jardins-ole/admin/crm/clients?limit=1000"),
          api.get("/admin/team"),
        ]);
        setClients(c.data || []);
        setTeam(t.data || []);
      } catch {
        toast.error("Erreur de chargement des références");
      }
    })();
  }, []);

  // Load worksite if editing
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/jardins-ole/admin/crm/worksites/${wid}`);
        setData({ ...empty, ...r.data });
      } catch {
        toast.error("Chantier introuvable");
        navigate("/admin/chantiers");
      } finally {
        setLoading(false);
      }
    })();
  }, [wid]);

  const save = async () => {
    if (!data.client_id) return toast.error("Sélectionnez un client");
    if (!data.title?.trim()) return toast.error("Titre obligatoire");
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.realization_id;
      let res;
      if (isNew) {
        res = await api.post("/jardins-ole/admin/crm/worksites", payload);
        toast.success("Chantier créé");
        navigate(`/admin/chantiers/${res.data.id}`, { replace: true });
      } else {
        res = await api.put(`/jardins-ole/admin/crm/worksites/${wid}`, payload);
        setData({ ...empty, ...res.data });
        toast.success("Enregistré");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhotos = async (files, kind) => {
    if (!files || files.length === 0) return;
    const uploaded = [];
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      try {
        const r = await axios.post(
          `${BACKEND}/api/jardins-ole/admin/crm/uploads/worksite-photo`,
          fd,
          { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
        );
        uploaded.push(r.data.url);
      } catch {
        toast.error(`Échec upload ${f.name}`);
      }
    }
    if (uploaded.length === 0) return;
    setData((d) => ({
      ...d,
      [kind === "before" ? "before_photos" : "after_photos"]: [
        ...(kind === "before" ? d.before_photos : d.after_photos),
        ...uploaded,
      ],
    }));
    toast.success(`${uploaded.length} photo(s) ajoutée(s)`);
  };

  const removePhoto = (kind, idx) => {
    setData((d) => ({
      ...d,
      [kind === "before" ? "before_photos" : "after_photos"]:
        (kind === "before" ? d.before_photos : d.after_photos).filter((_, i) => i !== idx),
    }));
  };

  const toggleTeam = (id) => {
    setData((d) => ({
      ...d,
      team_member_ids: d.team_member_ids.includes(id)
        ? d.team_member_ids.filter((x) => x !== id)
        : [...d.team_member_ids, id],
    }));
  };

  const togglePublish = async () => {
    if (isNew) return toast.error("Enregistrez d'abord le chantier");
    if (data.is_published) {
      if (!window.confirm("Retirer la publication de la réalisation liée ?")) return;
      setPublishing(true);
      try {
        await api.post(`/jardins-ole/admin/crm/worksites/${wid}/unpublish`);
        toast.success("Dépublié");
        const r = await api.get(`/jardins-ole/admin/crm/worksites/${wid}`);
        setData({ ...empty, ...r.data });
      } catch {
        toast.error("Erreur");
      } finally {
        setPublishing(false);
      }
    } else {
      if (!data.publish_consent) return toast.error("Cochez le consentement client d'abord");
      if ((data.after_photos || []).length === 0) return toast.error("Ajoutez au moins une photo après");
      // Save current first to ensure backend has fresh data
      await save();
      setPublishing(true);
      try {
        await api.post(`/jardins-ole/admin/crm/worksites/${wid}/publish`);
        toast.success("Publié dans les Réalisations");
        const r = await api.get(`/jardins-ole/admin/crm/worksites/${wid}`);
        setData({ ...empty, ...r.data });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur publication");
      } finally {
        setPublishing(false);
      }
    }
  };

  const submitNewClient = async () => {
    if (!newClient.first_name.trim() && !newClient.company_name.trim()) {
      return toast.error(newClient.type === "pro" ? "Raison sociale ou prénom requis" : "Prénom requis");
    }
    setCreatingClient(true);
    try {
      const payload = { ...newClient, first_name: newClient.first_name.trim() || newClient.company_name.trim() };
      const r = await api.post("/jardins-ole/admin/crm/clients", payload);
      const created = r.data;
      setClients((prev) => [created, ...prev]);
      setData((d) => ({ ...d, client_id: created.id }));
      setNewClientOpen(false);
      setNewClient({ type: "particulier", first_name: "", last_name: "", email: "", phone: "", company_name: "" });
      toast.success("Client créé et sélectionné");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de création");
    } finally {
      setCreatingClient(false);
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center text-[hsl(var(--muted-foreground))]">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
    </div>;
  }

  return (
    <div className="p-6 space-y-5" data-testid="chantier-edit-admin">
      <div className="flex items-center gap-3">
        <Link to="/admin/chantiers"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button></Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HardHat className="w-6 h-6" /> {isNew ? "Nouveau chantier" : (data.title || "Chantier")}
          </h1>
        </div>
        <Button onClick={save} disabled={saving} variant="outline" data-testid="chantier-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
        {!isNew && (
          <Button onClick={togglePublish} disabled={publishing}
            className={data.is_published ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
            data-testid="chantier-publish-btn">
            {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
              (data.is_published ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />)}
            {data.is_published ? "Dépublier" : "Publier dans Réalisations"}
          </Button>
        )}
      </div>

      <Card className="p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Client *</Label>
            <div className="flex gap-2 mt-1">
              <select
                value={data.client_id}
                onChange={(e) => setData({ ...data, client_id: e.target.value })}
                className="flex-1 border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white"
                data-testid="chantier-client-select"
              >
                <option value="">— Sélectionner —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.type === "pro" && c.company_name ? c.company_name : `${c.first_name} ${c.last_name}`.trim()}
                    {c.address_city ? ` · ${c.address_city}` : ""}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                onClick={() => setNewClientOpen(true)}
                data-testid="chantier-new-client-btn"
                title="Créer un nouveau client"
              >
                <UserPlus className="w-4 h-4 mr-1.5" /> Nouveau
              </Button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Titre du chantier *</Label>
            <Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} placeholder="ex: Création jardin méditerranéen 250 m²" data-testid="chantier-title-input" />
          </div>
          <div>
            <Label>Type de prestation</Label>
            <Input value={data.service_type} onChange={(e) => setData({ ...data, service_type: e.target.value })} placeholder="ex: Conception · Plantation · Maçonnerie" />
          </div>
          <div>
            <Label>Durée (jours)</Label>
            <Input type="number" min={0} value={data.duration_days} onChange={(e) => setData({ ...data, duration_days: parseInt(e.target.value) || 0 })} />
          </div>
          <div>
            <Label>Ville / Localité (publiée)</Label>
            <Input value={data.location_city} onChange={(e) => setData({ ...data, location_city: e.target.value })} placeholder="ex: Istres" />
          </div>
          <div>
            <Label>Code postal</Label>
            <Input value={data.location_postal_code} onChange={(e) => setData({ ...data, location_postal_code: e.target.value })} />
          </div>
          <div>
            <Label><Calendar className="w-3 h-3 inline mr-1" />Date début</Label>
            <Input type="date" value={data.started_at?.slice(0, 10) || ""} onChange={(e) => setData({ ...data, started_at: e.target.value })} />
          </div>
          <div>
            <Label><Calendar className="w-3 h-3 inline mr-1" />Date fin</Label>
            <Input type="date" value={data.ended_at?.slice(0, 10) || ""} onChange={(e) => setData({ ...data, ended_at: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>Contexte du projet</Label>
            <Textarea rows={4} value={data.context} onChange={(e) => setData({ ...data, context: e.target.value })} placeholder="Description du contexte, contraintes, demande client..." data-testid="chantier-context-input" />
          </div>
          <div className="md:col-span-2">
            <Label>Bénéfices client (publié)</Label>
            <Textarea rows={3} value={data.benefits} onChange={(e) => setData({ ...data, benefits: e.target.value })} placeholder="Résultats apportés au client : gain de temps, esthétique, valeur, etc." />
          </div>
        </div>
      </Card>

      {/* Team members */}
      <Card className="p-5">
        <Label><Users2 className="w-3 h-3 inline mr-1" />Intervenants</Label>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="chantier-team-list">
          {team.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">Aucun membre d'équipe enregistré.</div>}
          {team.map((m) => {
            const selected = data.team_member_ids.includes(m.id);
            const label = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email || m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => toggleTeam(m.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selected
                    ? "bg-[color:var(--brand-sky)] text-white border-[color:var(--brand-sky)]"
                    : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                }`}
                data-testid={`team-toggle-${m.id}`}
              >
                {selected ? "✓ " : ""}{label}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Photos */}
      <div className="grid md:grid-cols-2 gap-4">
        <PhotoSection
          title="Photos AVANT (optionnel)"
          photos={data.before_photos}
          onUpload={(files) => uploadPhotos(files, "before")}
          onRemove={(i) => removePhoto("before", i)}
          inputRef={beforeRef}
          testidPrefix="before"
        />
        <PhotoSection
          title="Photos APRÈS (publiées)"
          photos={data.after_photos}
          onUpload={(files) => uploadPhotos(files, "after")}
          onRemove={(i) => removePhoto("after", i)}
          inputRef={afterRef}
          testidPrefix="after"
        />
      </div>

      {/* Notes & publication consent */}
      <Card className="p-5 space-y-4">
        <div>
          <Label>Notes internes</Label>
          <Textarea rows={4} value={data.internal_notes} onChange={(e) => setData({ ...data, internal_notes: e.target.value })} placeholder="Visibles uniquement par l'équipe (non publié)." data-testid="chantier-internal-notes" />
        </div>
        <label className="flex items-start gap-2 cursor-pointer p-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))]/50">
          <input
            type="checkbox"
            checked={data.publish_consent}
            onChange={(e) => setData({ ...data, publish_consent: e.target.checked })}
            className="mt-0.5 w-4 h-4"
            data-testid="chantier-consent-checkbox"
          />
          <div>
            <div className="font-medium text-sm flex items-center gap-1">
              {data.publish_consent ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
              Le client a donné son accord pour la publication
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Réalisation anonymisée : ville, contexte, durée, bénéfices, photos APRÈS uniquement. Le nom du client n'est jamais affiché.
            </div>
          </div>
        </label>
      </Card>

      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
        <DialogContent data-testid="chantier-new-client-modal">
          <DialogHeader>
            <DialogTitle>Nouveau client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Type</Label>
              <div className="flex gap-2 mt-1">
                {[
                  ["particulier", "Particulier"],
                  ["pro", "Pro"],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setNewClient({ ...newClient, type: v })}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium ${
                      newClient.type === v
                        ? "bg-[color:var(--brand-sky)] text-white border-[color:var(--brand-sky)]"
                        : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                    }`}
                    data-testid={`chantier-newclient-type-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {newClient.type === "pro" && (
              <div>
                <Label>Raison sociale</Label>
                <Input value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} data-testid="chantier-newclient-company" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Prénom{newClient.type !== "pro" ? " *" : ""}</Label>
                <Input value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })} data-testid="chantier-newclient-first" autoFocus />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })} data-testid="chantier-newclient-last" />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} data-testid="chantier-newclient-email" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="chantier-newclient-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewClientOpen(false)} disabled={creatingClient}>Annuler</Button>
            <Button onClick={submitNewClient} disabled={creatingClient} data-testid="chantier-newclient-submit">
              {creatingClient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Créer &amp; sélectionner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoSection({ title, photos, onUpload, onRemove, inputRef, testidPrefix }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <Label className="mb-0">{title}</Label>
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid={`${testidPrefix}-upload-btn`}>
          <Upload className="w-4 h-4 mr-1.5" /> Ajouter
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
        />
      </div>
      {photos.length === 0 ? (
        <div className="text-center py-8 text-[hsl(var(--muted-foreground))] border-2 border-dashed rounded-md">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
          Aucune photo
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid={`${testidPrefix}-photos-grid`}>
          {photos.map((url, i) => (
            <div key={i} className="relative aspect-square rounded-md overflow-hidden bg-[hsl(var(--muted))]/30 group">
              <img src={url.startsWith("http") ? url : `${BACKEND}${url}`} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity flex items-center justify-center"
                data-testid={`${testidPrefix}-remove-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
